import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronLeft,
  CheckCircle2,
  Loader2,
  Sparkles,
  ShieldCheck,
  Lock,
  Tag,
} from "lucide-react";

import { fetchChecker, postCheckoutQuote, fetchCheckStatus, validateDiscount, API } from "@/lib/api";
import { getCheckerIcon } from "@/lib/checker-icons";
import UploadZone from "@/components/UploadZone";
import { FlagsBENL } from "@/components/Flags";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { usePublicConfig } from "@/lib/usePublicConfig";

const STEPS = [
  { id: 1, title: "Upload document" },
  { id: 2, title: "Gegevens & context" },
  { id: 3, title: "Betalen" },
];

function fmt(cents) {
  return `€${(cents / 100).toFixed(2).replace(".", ",")}`;
}

export default function AnalyseFlow() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const cfg = usePublicConfig();

  const [checker, setChecker] = useState(null);
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [context, setContext] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [choice, setChoice] = useState("single"); // 'single' | 'bundle'
  const [agreedTerms, setAgreedTerms] = useState(false);

  useEffect(() => {
    fetchChecker(slug).then(setChecker).catch(console.error);
    window.scrollTo(0, 0);
  }, [slug]);

  if (!checker) {
    return <div className="max-w-3xl mx-auto px-6 py-32"><div className="h-8 w-2/3 bg-slate-200 rounded animate-pulse" /></div>;
  }

  const Icon = getCheckerIcon(checker.icon);
  const accent = checker.category_color;
  const singlePrice = cfg?.pricing?.single_cents || 499;
  const bundlePrice = cfg?.pricing?.bundle_cents || 999;
  const bundleSize = cfg?.pricing?.bundle_size || 3;
  const perCheckBundle = Math.round(bundlePrice / bundleSize);
  const bundleSavingsPct = Math.round((1 - perCheckBundle / singlePrice) * 100);
  const basePrice = choice === "bundle" ? bundlePrice : singlePrice;
  const canSubmit = (file || context.trim().length > 30) && /\S+@\S+\.\S+/.test(email);
  const canPay = canSubmit && agreedTerms;
  const finalPrice = discountInfo ? Math.max(0, Math.round(basePrice * (1 - discountInfo.percent_off / 100))) : basePrice;

  async function checkDiscount() {
    if (!discountCode.trim()) return;
    try {
      const info = await validateDiscount(discountCode);
      setDiscountInfo(info);
      toast({ title: `Code geldig — ${info.percent_off}% korting`, description: `Nieuwe prijs: ${fmt(Math.max(0, Math.round(basePrice * (1 - info.percent_off / 100))))}` });
    } catch (err) {
      setDiscountInfo(null);
      toast({ title: "Code ongeldig", description: err?.response?.data?.detail || "Probeer een andere code.", variant: "destructive" });
    }
  }

  async function startPayment() {
    if (!canPay) return;
    setSubmitting(true);
    try {
      // 3-pack bundle path: redirect via credits/bundle/purchase
      if (choice === "bundle") {
        try {
          const r = await fetch(`${API}/credits/bundle/purchase`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email,
              first_name: firstName,
              last_name: lastName,
            }),
          });
          const j = await r.json();
          if (j?.payment_url) {
            // Bewaar de pending check-context lokaal zodat de gebruiker op /credits direct de huidige check kan claimen
            sessionStorage.setItem("ce_bundle_pending_check", JSON.stringify({
              checker_slug: slug,
              extra_context: context,
              file_name: file?.name,
              email,
              first_name: firstName,
              last_name: lastName,
              bundle_id: j.bundle_id,
            }));
            window.location.href = j.payment_url;
            return;
          }
          toast({ title: "Bundel-betaling mislukt", description: j?.detail || "Probeer 1 analyse als alternatief.", variant: "destructive" });
        } catch (err) {
          toast({ title: "Kan bundel niet starten", description: "Probeer opnieuw.", variant: "destructive" });
        }
        setSubmitting(false);
        return;
      }
      const res = await postCheckoutQuote({
        slug,
        file,
        extraContext: context,
        email,
        firstName,
        lastName,
        discountCode: discountInfo ? discountCode : undefined,
      });
      if (res.free) {
        // 100% discount, go to result polling
        navigate(`/result/${res.check_id}?fresh=1`);
      } else if (res.payment_url) {
        window.location.href = res.payment_url;
      }
    } catch (err) {
      toast({
        title: "Kan niet starten",
        description: err?.response?.data?.detail || "Er ging iets mis. Probeer opnieuw.",
        variant: "destructive",
      });
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-secondary min-h-screen" data-testid={`analyse-flow-${slug}`}>
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to={`/check/${slug}`} className="inline-flex items-center gap-1 text-sm text-ink-soft hover:text-ink mb-6">
          <ChevronLeft className="h-4 w-4" /> Terug naar {checker.name}
        </Link>

        <div className="flex items-center gap-4 mb-10">
          <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white shadow-sm" style={{ background: accent }}>
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-ink-soft">{checker.category_label} · CheckExpert</p>
            <h1 className="font-display text-3xl font-bold text-ink tracking-tight">{checker.name}</h1>
          </div>
        </div>

        <ol className="flex items-center gap-2 mb-10" data-testid="step-indicator">
          {STEPS.map((s, i) => (
            <li key={s.id} className="flex items-center gap-2 flex-1">
              <div
                className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  step >= s.id ? "text-white" : "bg-white text-ink-soft border border-border"
                }`}
                style={step >= s.id ? { background: accent } : undefined}
              >
                {step > s.id ? <CheckCircle2 className="h-4 w-4" /> : s.id}
              </div>
              <span className={`text-xs sm:text-sm font-medium ${step >= s.id ? "text-ink" : "text-ink-soft"} hidden sm:block`}>
                {s.title}
              </span>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border ml-2" />}
            </li>
          ))}
        </ol>

        <div className="rounded-3xl bg-white border border-border p-8 shadow-sm">
          {step === 1 && (
            <div data-testid="step-upload">
              <h2 className="font-display text-2xl font-bold text-ink">Upload je document</h2>
              <p className="text-ink-soft mt-2">PDF, Word (.docx), foto of getypte tekst — versleuteld geüpload, 30 dagen bewaard.</p>
              <div className="mt-6">
                <UploadZone value={file} onChange={setFile} accent={accent} />
              </div>
              <div className="mt-8 flex items-center justify-between">
                <span className="text-xs text-ink-soft flex items-center gap-2"><Lock className="h-3.5 w-3.5" /> Versleutelde upload via HTTPS</span>
                <button
                  onClick={() => setStep(2)}
                  disabled={!file}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: accent }}
                  data-testid="step1-next"
                >
                  Volgende <ArrowRight className="h-4 w-4" />
                </button>
              </div>
              {!file && (
                <p className="mt-3 text-xs text-ink-subtle">Upload eerst je document om door te gaan.</p>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-context">
              <h2 className="font-display text-2xl font-bold text-ink">Jouw gegevens &amp; context</h2>
              <p className="text-ink-soft mt-2">We mailen het rapport ook naar dit adres. Extra context maakt de analyse scherper.</p>

              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <div>
                  <Label htmlFor="first">Voornaam</Label>
                  <Input id="first" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="first-name" />
                </div>
                <div>
                  <Label htmlFor="last">Achternaam</Label>
                  <Input id="last" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="last-name" />
                </div>
              </div>
              <div className="mt-4">
                <Label htmlFor="email">E-mailadres *</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="email" />
              </div>
              <div className="mt-4">
                <Label htmlFor="context">Extra context (optioneel, of plak hier de tekst van je document)</Label>
                <Textarea
                  id="context"
                  rows={5}
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder="Bv: Ik huur deze studio sinds 2023, verhuurder rekent nu €450 voor herstellingen…"
                  data-testid="context-textarea"
                />
              </div>

              <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
                <button onClick={() => setStep(1)} className="text-sm text-ink-soft hover:text-ink" data-testid="step2-back">← Terug</button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!canSubmit}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold disabled:opacity-40"
                  style={{ background: accent }}
                  data-testid="step2-next"
                >
                  Volgende <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-payment">
              <h2 className="font-display text-2xl font-bold text-ink">Bevestig en betaal</h2>
              <p className="text-ink-soft mt-2">Eenmalige betaling via MultiSafepay (Bancontact, iDEAL, Wero, kaart, Apple Pay, Google Pay).</p>

              {/* Pakket-keuze upsell */}
              <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="pack-choice">
                <button
                  type="button"
                  onClick={() => setChoice("single")}
                  className={`text-left rounded-2xl border-2 p-5 transition-all ${choice === "single" ? "border-ink bg-ink/[0.03]" : "border-border bg-white hover:border-ink-soft"}`}
                  data-testid="choose-single"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs uppercase tracking-wider font-bold text-ink-soft">1 analyse</span>
                    <span className={`h-4 w-4 rounded-full ${choice === "single" ? "bg-ink" : "border-2 border-border"}`} />
                  </div>
                  <div className="font-display text-3xl font-bold text-ink mt-3">{fmt(singlePrice)}</div>
                  <p className="text-xs text-ink-soft mt-1">Eenmalig · BTW inbegrepen</p>
                  <ul className="text-xs text-ink-soft mt-3 space-y-1">
                    <li>• Deze {checker.name}</li>
                    <li>• 30 dagen toegang</li>
                  </ul>
                </button>

                <button
                  type="button"
                  onClick={() => setChoice("bundle")}
                  className={`text-left rounded-2xl border-2 p-5 transition-all relative overflow-hidden ${choice === "bundle" ? "border-[#002FA7] bg-blue-50/40 shadow-lg" : "border-border bg-white hover:border-[#002FA7]/60"}`}
                  data-testid="choose-bundle"
                >
                  <span className="absolute top-0 right-0 bg-[#002FA7] text-white text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-bl-xl">
                    Aanbevolen · −{bundleSavingsPct}%
                  </span>
                  <div className="flex items-center justify-between gap-3 mt-1">
                    <span className="text-xs uppercase tracking-wider font-bold text-[#002FA7]">{bundleSize}-pack bundel</span>
                    <span className={`h-4 w-4 rounded-full ${choice === "bundle" ? "bg-[#002FA7]" : "border-2 border-border"}`} />
                  </div>
                  <div className="flex items-baseline gap-2 mt-3">
                    <div className="font-display text-3xl font-bold text-ink">{fmt(bundlePrice)}</div>
                    <div className="text-xs text-ink-subtle line-through">{fmt(singlePrice * bundleSize)}</div>
                  </div>
                  <p className="text-xs text-ink-soft mt-1">Slechts <b className="text-ink">{fmt(perCheckBundle)}</b> per check</p>
                  <ul className="text-xs text-ink-soft mt-3 space-y-1">
                    <li>• 3 checks — alle 25 types vrij te kiezen</li>
                    <li>• Credits 12 maanden geldig</li>
                    <li className="text-emerald-700 font-semibold">• Je bespaart {fmt(singlePrice * bundleSize - bundlePrice)}</li>
                  </ul>
                </button>
              </div>

              {choice === "bundle" && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                  <Sparkles className="h-3.5 w-3.5" /> Goede keuze — je {checker.name} wordt direct gestart, de overige {bundleSize - 1} credits staan op je credits-pagina klaar.
                </div>
              )}

              <div className="mt-6 rounded-2xl bg-secondary p-6 border border-border space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-ink-soft">{choice === "bundle" ? `${bundleSize}-pack credits (incl. ${checker.name} nu)` : `${checker.name} analyse`}</span>
                  <span className="text-ink font-semibold">{fmt(basePrice)}</span>
                </div>
                {discountInfo && (
                  <div className="flex justify-between text-sm text-emerald-700">
                    <span>Korting ({discountCode}) — {discountInfo.percent_off}%</span>
                    <span>−{fmt(basePrice - finalPrice)}</span>
                  </div>
                )}
                <div className="border-t border-border pt-3 flex justify-between text-base">
                  <span className="font-semibold text-ink">Te betalen</span>
                  <span className="font-display text-2xl font-bold text-ink">{fmt(finalPrice)}</span>
                </div>
              </div>

              <div className="mt-6 grid sm:grid-cols-[1fr_auto] gap-3">
                <div>
                  <Label htmlFor="code">Kortingscode</Label>
                  <Input id="code" placeholder="CE-XXXXXXXX" value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} data-testid="discount-code" />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={checkDiscount}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-border text-ink text-sm font-semibold hover:border-ink-soft"
                    data-testid="apply-discount"
                  >
                    <Tag className="h-4 w-4" /> Toepassen
                  </button>
                </div>
              </div>

              <div className={`mt-6 rounded-2xl border-2 p-5 transition-all ${agreedTerms ? "border-emerald-500 bg-emerald-50/40" : "border-amber-400 bg-amber-50/40"}`} data-testid="terms-consent-box">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedTerms}
                    onChange={(e) => setAgreedTerms(e.target.checked)}
                    required
                    className="mt-1 h-5 w-5 rounded border-2 border-ink cursor-pointer accent-emerald-600"
                    data-testid="agree-terms-checkbox"
                  />
                  <div className="text-sm text-ink leading-relaxed">
                    <b>Ik ga akkoord</b> met de directe start van de analyse en bevestig dat ik
                    {" "}<Link to="/algemene-voorwaarden" className="text-[#002FA7] font-semibold underline-offset-2 hover:underline">de algemene voorwaarden</Link>{" "}
                    heb gelezen en aanvaard. Ik begrijp dat <b>na bestelling geen terugbetaling mogelijk is</b> — ik doe hierbij vrijwillig afstand van mijn herroepingsrecht (art. VI.53 WER).
                    <span className="block mt-1 text-xs text-ink-soft">CheckExpert is uitsluitend voor particulieren (B2C).</span>
                  </div>
                </label>
              </div>

              <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                <button onClick={() => setStep(2)} className="text-sm text-ink-soft hover:text-ink">← Terug</button>
                <button
                  onClick={startPayment}
                  disabled={submitting || !canPay}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: accent }}
                  data-testid="pay-now"
                  title={!agreedTerms ? "Vink eerst het akkoord aan" : undefined}
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {finalPrice === 0 ? "Start gratis analyse" : `Betaal ${fmt(finalPrice)}`}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 text-center text-xs text-ink-soft">
          <ShieldCheck className="h-3.5 w-3.5 inline-block mr-1" /> Beveiligd via MultiSafepay · 256-bit encryptie · GDPR-conform
        </div>
      </div>
    </div>
  );
}

// Polling component used by Result page (exported for reuse)
export function useCheckPolling(id, enabled = true) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!id || !enabled) return;
    let cancelled = false;
    let interval;
    async function tick() {
      try {
        const s = await fetchCheckStatus(id);
        if (cancelled) return;
        setStatus(s);
        if (s.ready) {
          clearInterval(interval);
        }
      } catch (err) {
        // ignore
      }
    }
    tick();
    interval = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id, enabled]);
  return status;
}
